import React, { useState, useEffect, useMemo, Dispatch, useRef, useCallback } from 'react';
import { Box, Button, IconButton, Tooltip, Stack } from '@mui/material';
import {
  Edit as EditIcon,
  Done as DoneIcon,
  CachedOutlined as CachedIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import DisplayResponseFooter from '../Footers/DisplayResponseFooter';
import { modalTitle } from '../config';
import HtmlEditor from '../../../NewUI/Components/Utilities/HtmlEditor';
import dompurify from 'dompurify';
import { AIStudioAction, AIStudioState } from '../reducer';
import { classes } from '../styles';
import { classes as sharedClasses } from '../../SharedComponents/styles';
import CopyableQuestions from '../../SharedComponents/CopyableQuestions';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { LoadingOverlay } from '../components/LoadingOverlay';
import ConfettiExplosion from 'react-confetti-explosion';
import { StartAgainIcon } from '../../../NewUI/Components/Icons';

declare global {
  interface Window {
    tinyMCE?: {
      majorVersion: string;
    };
  }
}

interface TranslationLabels {
  regenerate: string;
  startAgain: string;
  tooltip: string;
}

const getTranslationLabels = (isFrench: boolean, isCopied: boolean): TranslationLabels => ({
  regenerate: isFrench ? 'Regénérer' : 'Regenerate',
  startAgain: isFrench ? 'Recommencer' : 'Start again',
  tooltip: isCopied ? (isFrench ? 'Copié' : 'Copied') : isFrench ? 'Copier' : 'Copy'
});

const DisplayDescription = ({
  dispatch,
  aiStudioState,
  generateDescription,
  generateQuestions,
  jobFlow,
  setGeneratedContent,
  handleClose,
  isSidebarTrigger = false
}: {
  dispatch: Dispatch<AIStudioAction>;
  aiStudioState: AIStudioState;
  generateDescription: (feedbackInput?: string) => void;
  generateQuestions: (feedbackInput?: string) => void;
  jobFlow?: boolean;
  setGeneratedContent?: (description: string) => void;
  handleClose: () => void;
  isSidebarTrigger?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [editableDescription, setEditableDescription] = useState(aiStudioState.generatedResponse);
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [playConfetti, setPlayConfetti] = useState(true);

  const translationLabels = useMemo(
    () => getTranslationLabels(aiStudioState.aiStudioFrenchEnabled, copied),
    [aiStudioState.aiStudioFrenchEnabled, copied]
  );

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  useEffect(() => {
    setPlayConfetti(true);
    const confettiTimeout = setTimeout(() => {
      setPlayConfetti(false);
    }, 4100);
    return () => clearTimeout(confettiTimeout);
  }, []);

  useEffect(() => {
    if (aiStudioState.generatedResponse) {
      setEditableDescription(aiStudioState.generatedResponse);
    }
  }, [aiStudioState.generatedResponse]);

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  useEffect(() => {
    const shouldScroll =
      !aiStudioState.isLoading &&
      aiStudioState.generationType === 'questions' &&
      aiStudioState.hasFeedbackInput;

    if (shouldScroll) {
      scrollToBottom();
    }
  }, [
    aiStudioState.isLoading,
    aiStudioState.generationType,
    aiStudioState.hasFeedbackInput,
    scrollToBottom
  ]);

  const regenerationFunction = {
    description: () => generateDescription(''),
    questions: () => generateQuestions('')
  };

  const displayResponseContent = useMemo(() => {
    if (aiStudioState.generationType === 'description') {
      return (
        <Box sx={{ position: 'relative', width: '100%', height: '500px' }}>
          {isEditing ? (
            <HtmlEditor
              body={editableDescription}
              onChange={setEditableDescription}
              maxHeight={500}
              minHeight={500}
            />
          ) : (
            <Box
              sx={classes.generatedResponseContainer}
              dangerouslySetInnerHTML={{
                __html: dompurify.sanitize(editableDescription)
              }}
            />
          )}
          <Box
            sx={{
              ...sharedClasses.floatingActionButton,
              ...(window?.tinyMCE?.majorVersion === '4' && { display: 'none' })
            }}
            onClick={() => {
              setIsEditing((prev) => !prev);
            }}
          >
            {isEditing ? <DoneIcon /> : <EditIcon />}
          </Box>
          <LoadingOverlay
            isLoading={aiStudioState.isLoading}
            generationType={aiStudioState.generationType}
          />
        </Box>
      );
    } else {
      return (
        <Box sx={{ position: 'relative', width: '100%', height: '490px' }}>
          <CopyableQuestions
            questions={aiStudioState.generatedResponse}
            maxHeight="490px"
            newQuestions={aiStudioState.newQuestions}
            containerRef={containerRef}
          />
          <LoadingOverlay
            isLoading={aiStudioState.isLoading}
            generationType={aiStudioState.generationType}
          />
        </Box>
      );
    }
  }, [
    aiStudioState.generatedResponse,
    aiStudioState.generationType,
    editableDescription,
    isEditing,
    aiStudioState.isLoading,
    aiStudioState.newQuestions
  ]);

  const handleAddToJobClick = () => {
    setGeneratedContent?.(editableDescription);
    handleClose();
  };

  const renderPrimaryButton = () => {
    if (aiStudioState.generationType !== 'description') return null;

    if (jobFlow) {
      return (
        <Button
          id="save-tlp-new-job"
          sx={{ ...classes.footerContinueButton, minWidth: '146px' }}
          endIcon={<DoneIcon />}
          onClick={handleAddToJobClick}
          disabled={aiStudioState.isLoading}
        >
          Add to job
        </Button>
      );
    }

    return (
      <CopyToClipboard text={editableDescription} onCopy={handleCopy}>
        <Tooltip title={`${translationLabels.tooltip} HTML`} placement="top">
          <IconButton
            id="copy-tlp-html"
            sx={classes.actionButton}
            size="small"
            disabled={aiStudioState.isLoading}
          >
            {copied ? <DoneIcon /> : <CodeIcon />}
          </IconButton>
        </Tooltip>
      </CopyToClipboard>
    );
  };

  return (
    <Box sx={classes.generatedContainer}>
      {playConfetti && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <ConfettiExplosion
            force={0.5}
            duration={4000}
            particleCount={120}
            height={1000}
            width={1000}
          />
        </Box>
      )}
      <Box sx={classes.descriptionHeader}>
        <Box sx={classes.descriptionHeaderText}>
          {`${aiStudioState.aiStudioFrenchEnabled ? 'Ton' : 'Your'} ${
            modalTitle[aiStudioState.aiStudioFrenchEnabled ? 'french' : 'english'][
              aiStudioState.generationType
            ]
          }`}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip placement="top" title={translationLabels.regenerate} arrow>
            <IconButton
              id={`regenerate-${aiStudioState.generationType}`}
              disabled={aiStudioState.isLoading}
              onClick={
                regenerationFunction[
                  aiStudioState.generationType as keyof typeof regenerationFunction
                ]
              }
              sx={classes.actionButton}
              size="small"
            >
              <CachedIcon />
            </IconButton>
          </Tooltip>
          {!isSidebarTrigger && (
            <Tooltip placement="top" title={translationLabels.startAgain} arrow>
              <IconButton
                id={`start-again-${aiStudioState.generationType}`}
                disabled={aiStudioState.isLoading}
                onClick={() =>
                  dispatch({
                    type: 'SET_CURRENT_STEP',
                    payload: aiStudioState.generationType === 'description' ? 1 : 5
                  })
                }
                sx={{
                  ...classes.actionButton,
                  '& .start-again-icon path': {
                    fill: aiStudioState.isLoading ? '#939393' : '#7A8EF1'
                  },
                  '&:hover .start-again-icon path': {
                    fill: aiStudioState.isLoading ? '#939393' : '#2D388D'
                  }
                }}
                size="small"
              >
                <StartAgainIcon className="start-again-icon" />
              </IconButton>
            </Tooltip>
          )}
          {renderPrimaryButton()}
          {isSidebarTrigger && (
            <Button
              id="save-tlp-new-job"
              sx={{ ...classes.footerContinueButton, minWidth: '146px' }}
              endIcon={<DoneIcon />}
              onClick={() =>
                dispatch({
                  type: 'SET_CURRENT_STEP',
                  payload: aiStudioState.generationType === 'description' ? 1 : 5
                })
              }
              disabled={aiStudioState.isLoading}
            >
              {translationLabels.startAgain}
            </Button>
          )}
        </Stack>
      </Box>
      {displayResponseContent}
      <DisplayResponseFooter
        aiStudioState={aiStudioState}
        dispatch={dispatch}
        generateDescription={generateDescription}
        generateQuestions={generateQuestions}
      />
    </Box>
  );
};

export default DisplayDescription;
